import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo } from 'react';

import {
  EMPTY_HIDDEN_MAIL_FOLDER_DOC_IDS,
  resolveHiddenMailFolderDocIds,
} from './mail-folder-visibility-rules';
import { useWorkspaceMailAccounts } from './use-workspace-mail';

export {
  filterDocGroupsForHiddenMailFolderDocIds,
  filterExplorerGroupsForHiddenMailFolderDocIds,
  resolveHiddenMailFolderDocIds,
} from './mail-folder-visibility-rules';

export function useHiddenMailFolderDocIds(): ReadonlySet<string> {
  const workspace = useService(WorkspaceService).workspace;
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const accounts = useWorkspaceMailAccounts(
    workspace.flavour === 'local' ? null : workspace.id
  );

  useEffect(() => {
    if (workspace.flavour === 'local') return;
    void accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
  }, [accessibleFolderCache, workspace.flavour]);

  return useMemo(() => {
    if (workspace.flavour === 'local') return EMPTY_HIDDEN_MAIL_FOLDER_DOC_IDS;
    const visibleMailboxRootDocIds = new Set(
      (accounts.data ?? [])
        .filter(account => account.showInWorkspace)
        .map(account => account.accessDocId)
    );
    const hiddenDocIds = new Set(
      (accounts.data ?? [])
        .filter(account => !account.showInWorkspace)
        .map(account => account.accessDocId)
    );
    for (const docId of resolveHiddenMailFolderDocIds(
      accessibleFolders,
      visibleMailboxRootDocIds
    )) {
      hiddenDocIds.add(docId);
    }
    return hiddenDocIds;
  }, [accessibleFolders, accounts.data, workspace.flavour]);
}
